import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import _ from 'underscore';
import Promise from 'bluebird';

import { checkVideosIntermediateStatus, FolderAssetType } from '../../model';
import { getTaskPoller } from '../task-poller';

// below fields will be impacted along with status change
const VideoChangedFieldsWithStatusUpdate = [
  'thumbnailUrl',
  'url',
  'width',
  'height',
  'durationInMS',
  'status',
];

export const useVideoTaskPoller = ({
  repository,
  logger,
  assets,
  interval,
}) => {
  const latestTimestampRef = useRef(0);
  const [videoUpdated, setVideoUpdated] = useState({}); // sample: { 123: { .. } }
  const getVideoTaskStatus = useCallback((videoTasks) => {
    const requestTimestamp = Date.now();
    const videosToCheck = _.keys(videoTasks);

    return repository.updateVideoStatusByIds(videosToCheck).then((response) => {
      // if multipe requests are sent out, ensure to keep the result from the latest request
      let notFinishedTaskList = {};
      let newVideoUpdated = {};
      if (requestTimestamp > latestTimestampRef.current) {
        const responseKeys = [];

        response.forEach((item) => {
          responseKeys.push(item.id);
          newVideoUpdated = {
            ...newVideoUpdated,
            [item.id]: _.pick(item, VideoChangedFieldsWithStatusUpdate),
          };

          if (item && checkVideosIntermediateStatus(item)) {
            notFinishedTaskList = { ...notFinishedTaskList, [item.id]: item.status };
          }
        });

        // eslint-disable-next-line no-param-reassign
        latestTimestampRef.current = requestTimestamp;
      }

      setVideoUpdated(newVideoUpdated);

      const taskCount = Object.keys(notFinishedTaskList).length;

      return { totalCount: taskCount };
    }).catch(() => {
      // log and ignore the error
      logger.logError('Failed to get videos task status.');
    });
  }, [logger, repository]);

  const newVideoTasks = useRef({});
  const isPollerRunning = useRef(false);

  const currentTaskPoller = useRef(null);

  const startVideoTaskStatusPoller = useCallback(() => {
    const taskPoller = getTaskPoller({
      task: () => {
        isPollerRunning.current = true;

        const queryPromise = _.isEmpty(newVideoTasks.current)
          ? Promise.resolve({ totalCount: 0 })
          : getVideoTaskStatus(newVideoTasks.current);

        return queryPromise.then((res) => {
          const { totalCount } = res || {};

          const isFinished = totalCount === 0;

          if (isFinished) {
            isPollerRunning.current = false;
          }

          return { isFinished };
        });
      },
      interval,
    });
    taskPoller.start();
    const stopPoller = () => {
      taskPoller.stop();
      isPollerRunning.current = false;
    };
    return { stopPoller };
  }, [getVideoTaskStatus, interval]);

  useEffect(() => {
    newVideoTasks.current = assets ? assets.reduce((prev, asset) => {
      if (asset.folderAssetType === FolderAssetType.Video && checkVideosIntermediateStatus(asset)) {
        return { ...prev, [asset.id]: asset.status };
      }
      return prev;
    }, {}) : {};

    // start pollerif there are no running poller
    if (!isPollerRunning.current && !_.isEmpty(newVideoTasks.current)) {
      const { stopPoller } = startVideoTaskStatusPoller();
      currentTaskPoller.current = stopPoller;
    }

    // stop poller when component unmount.
    return () => {
      if (currentTaskPoller.current && isPollerRunning.current) {
        currentTaskPoller.current();
      }
    };
  }, [assets, startVideoTaskStatusPoller]);

  const returnVal = useMemo(() => ({
    videoUpdated,
  }), [videoUpdated]);

  return returnVal;
};
